// A lead's preview, on its page: the header button (Build a preview, or the
// preview's page once there is a live one; owner, 2026-11-03: not at the bottom
// of the page) and the card with its address, end date, status and sign-ins, Open
// and Copy sign-in link. A do-not-contact lead can't get a new one. An ended
// preview is history: the card shows the latest, and Build a preview comes back.
// Both read one query, so one request.

import { Link } from 'react-router-dom'
import { api } from '@/api'
import type { Lead } from '@/api/types'
import { ButtonLink, Card, CardHeader, CopyButton, ErrorState, KeyValueList, LoadingState, RelativeTime } from '@/components/ui'
import { formatDate } from '@/lib/format'
import { addressText, daysLeftText } from '@/lib/previews'
import { useQuery } from '@/lib/query'
import { OpenPreviewButton, PreviewStatusBadge } from './previewShared'

type PreviewLead = Pick<Lead, 'id' | 'status' | 'suppressed'>

function useLeadPreview(lead: PreviewLead) {
  return useQuery(['growth', 'lead-preview', lead.id], (signal) => api.growth.previews.list({ lead_id: lead.id }, signal))
}

/** The header button: Build a preview, or Open preview once there is one. */
export function LeadPreviewAction({ lead }: { lead: PreviewLead }) {
  const { data } = useLeadPreview(lead)
  if (!data) return null
  const preview = data.previews[0]
  if (preview && (preview.status === 'active' || preview.status === 'claimed')) return <ButtonLink to={`/growth/previews/${preview.id}`}>See preview</ButtonLink>
  if (lead.status === 'do_not_contact' || lead.suppressed) return null
  return (
    <ButtonLink to={`/growth/leads/${lead.id}/preview`} variant="primary">
      Build a preview
    </ButtonLink>
  )
}

/** The preview's status and views; nothing until there is one (Build a preview is in the header). */
export function LeadPreviewCard({ lead }: { lead: PreviewLead }) {
  const { data, error, status, refetch } = useLeadPreview(lead)
  const preview = data?.previews[0]

  if (data && !preview) return null
  return (
    <Card>
      <CardHeader title="Preview" />
      {status === 'loading' ? (
        <LoadingState label="Loading preview…" />
      ) : status === 'error' && !data ? (
        <ErrorState error={error} title="Couldn't load the preview" onRetry={refetch} />
      ) : preview ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <PreviewStatusBadge preview={preview} />
            <Link to={`/growth/previews/${preview.id}`} className="break-all text-[13px] font-semibold underline underline-offset-2 hover:text-accent">
              {addressText(preview)}
            </Link>
          </div>
          <KeyValueList
            items={[
              { label: preview.status === 'active' ? 'Ends' : 'Ended', value: <>{formatDate(preview.expires_at)}{preview.status === 'active' ? ` (${daysLeftText(preview)})` : ''}</> },
              { label: 'Sign-ins', value: preview.view_count },
              { label: 'Last signed in', value: preview.last_viewed_at ? <RelativeTime value={preview.last_viewed_at} /> : 'Never' },
            ]}
          />
          {preview.status === 'active' && preview.sign_in_url ? (
            <div className="flex flex-wrap gap-2">
              <OpenPreviewButton preview={preview} size="sm" />
              <CopyButton value={preview.sign_in_url} label="Copy sign-in link" ariaLabel="Copy the lead's sign-in link" />
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}
